import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import IntegrationInstructions from '@mui/icons-material/IntegrationInstructions';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import PeopleIcon from '@mui/icons-material/People';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import RuleIcon from '@mui/icons-material/Rule';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import ConstructionIcon from '@mui/icons-material/Construction';
import WebhookIcon from '@mui/icons-material/Webhook';
import TuneIcon from '@mui/icons-material/Tune';
import HandshakeIcon from '@mui/icons-material/Handshake';
import simpleRestProvider from 'ra-data-simple-rest';
import { Admin, CustomRoutes, Resource } from 'react-admin';
import { Route } from 'react-router-dom';
import GoogleLoginPage from './Components/Auth/GoogleLoginPage';
import AppLayout from './Components/Layout/AppLayout';
import { getAuthProviderFetchJson } from './lib/Auth';
import { superAdminApiServer } from './lib/config';
import { getIntegrationConfigUpsertBody, normalizeIntegrationRecord } from './types/integrations';
import { partnerBody } from './lib/partners';
import GoogleAuthProvider from './lib/Providers/Auth/GoogleAuthProvider';
import BusinessRulesList from './Pages/BusinessRules/BusinessRulesList';
import BusinessRuleShow from './Pages/BusinessRules/BusinessRulesShow';
import CompanyList from './Pages/Companies/CompanyList';
import CompanyShow from './Pages/Companies/CompanyShow';
import { CustomerEdit } from './Pages/Customers/CustomerEdit';
import CustomerShow from './Pages/Customers/CustomerShow';
import CustomersList from './Pages/Customers/CustomersList';
import HcmList from './Pages/Hcm/HcmList';
import HcmShow from './Pages/Hcm/HcmShow';
import HcmEdit, { IntegrationStatus } from './Pages/Hcm/HcmEdit';
import EmployeeCacheDetail from './Pages/Hcm/EmployeeCacheDetail';
import RequestCacheDetail from './Pages/Hcm/RequestCacheDetail';
import DevicesList from './Pages/Devices/DevicesList';
import OrganizationsList from './Pages/Organizations/OrganizationsList';
import ProfileShow from './Pages/Profiles/ProfileShow';
import ProfilesList from './Pages/Profiles/ProfilesList';
import UsersList from './Pages/Users/UsersList';
import UsersShow from './Pages/Users/UsersShow';
import DashboardsList from './Pages/Dashboards/DashboardsList';
import CSToolsList from './Pages/CSTools/CSToolsList';
import DashboardEdit from './Pages/Dashboards/DashboardEdit';
import WebhooksList from './Pages/Webhooks/WebhooksList';
import WebhookShow from './Pages/Webhooks/WebhookShow';
import HolidayTemplatesCreate from './Pages/HolidayTemplates/HolidayTemplatesCreate';
import HolidayTemplatesEdit from './Pages/HolidayTemplates/HolidayTemplatesEdit';
import ScheduleTemplatesCreate from './Pages/ScheduleTemplates/ScheduleTemplatesCreate';
import ScheduleTemplatesEdit from './Pages/ScheduleTemplates/ScheduleTemplatesEdit';
import ApiCountersDashboard from './Pages/ApiCounters/ApiCountersDashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import CountryConfigsList from './Pages/CountryConfigs/CountryConfigsList';
import CountryConfigsCreate from './Pages/CountryConfigs/CountryConfigsCreate';
import CountryConfigsEdit from './Pages/CountryConfigs/CountryConfigsEdit';
import CountryConfigsShow from './Pages/CountryConfigs/CountryConfigsShow';
import BusinessRulesTemplatesCreate from './Pages/BusinessRulesTemplates/BusinessRulesTemplatesCreate';
import BusinessRulesTemplatesEdit from './Pages/BusinessRulesTemplates/BusinessRulesTemplatesEdit';
import RequestTypeTemplatesCreate from './Pages/RequestTypeTemplates/RequestTypeTemplatesCreate';
import RequestTypeTemplatesEdit from './Pages/RequestTypeTemplates/RequestTypeTemplatesEdit';
import RequestSubtypeTemplatesCreate from './Pages/RequestSubtypeTemplates/RequestSubtypeTemplatesCreate';
import RequestSubtypeTemplatesEdit from './Pages/RequestSubtypeTemplates/RequestSubtypeTemplatesEdit';
import DevToolsHome from './Pages/DevTools/DevToolsHome';
import RecalculatePage from './Pages/DevTools/Recalculate/RecalculatePage';
import PayPolicyTemplatesList from './Pages/PayPolicyTemplates/PayPolicyTemplatesList';
import PayPolicyTemplateEditor from './Pages/PayPolicyTemplates/PayPolicyTemplateEditor';
import TemplatesGate, { withTemplatesGate } from './Pages/PayPolicyTemplates/TemplatesGate';
import PartnersList from './Pages/Partners/PartnersList';
import PartnerShow from './Pages/Partners/PartnerShow';
import PartnerCreate from './Pages/Partners/PartnerCreate';
import PartnerEdit from './Pages/Partners/PartnerEdit';
import PartnerConfigEdit from './Pages/Partners/PartnerConfigEdit';

const GatedPayPolicyTemplatesList = withTemplatesGate(PayPolicyTemplatesList);

const dataProvider = simpleRestProvider(superAdminApiServer, getAuthProviderFetchJson);
//const dataProvider = crudProvider('http://127.0.0.1:3001');
/**
 * Convert a `File` object returned by the upload input into a base 64 string.
 * That's not the most optimized way to store images in production, but it's
 * enough to illustrate the idea of data provider decoration.
 */

const convertFileToBase64 = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(file.rawFile);
  });

const myDataProvider = {
  ...dataProvider,
  /** Core returns snake_case; RA form uses camelCase. Do not spread API record — forbidNonWhitelisted on core rejects extra keys. */
  mapCountryConfigRecord: (record: any) => {
    const r = record?.content != null ? record.content : record;
    return {
      id: String(r.country),
      uuid: r.uuid,
      country: String(r.country),
      cssUrl: r.css_url ?? null,
      payrollUrl: r.payroll_url ?? null,
      payrollUrlAuthStrategy: r.payroll_url_auth_strategy ?? null,
      extraOptions: r.extra_options ?? null,
      monthlyAccrualEnabled: r.extra_options?.settings?.monthlyAccrualEnabled ?? false
    };
  },
  countryConfigUpsertPayload: (data: any) => {
    const emptyToNull = (value: unknown): string | null => {
      if (value === undefined || value === null) return null;
      const s = String(value).trim();
      return s === '' ? null : s;
    };
    const optionalUrl = (value: unknown): string | null => {
      if (value === undefined || value === null) return null;
      const s = String(value).trim();
      return s === '' ? null : s;
    };
    const normalizeExtraOptions = (): Record<string, unknown> | null | undefined => {
      const value = data.extraOptions;
      if (value === undefined) return undefined;
      if (value == null || value === '') return null;
      if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value) as unknown;
          return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
        } catch {
          return null;
        }
      }
      return null;
    };
    const extra = normalizeExtraOptions();
    // `monthlyAccrualEnabled` is a virtual top-level form field (not bound to the
    // extraOptions JSON textarea). Merge it into extra_options.settings while
    // preserving every other extraOptions key and every other settings key.
    const baseExtra: Record<string, unknown> = extra != null ? extra : {};
    const existingSettings = typeof baseExtra.settings === 'object' && baseExtra.settings !== null && !Array.isArray(baseExtra.settings) ? (baseExtra.settings as Record<string, unknown>) : {};
    const mergedExtra: Record<string, unknown> = {
      ...baseExtra,
      settings: { ...existingSettings, monthlyAccrualEnabled: !!data.monthlyAccrualEnabled }
    };
    const countryRaw = data.country ?? data.id;
    return {
      country: String(countryRaw),
      cssUrl: optionalUrl(data.cssUrl),
      payrollUrl: optionalUrl(data.payrollUrl),
      payrollUrlAuthStrategy: emptyToNull(data.payrollUrlAuthStrategy),
      extraOptions: mergedExtra
    };
  },
  getList: (resource: any, params: any) => {
    if (resource === 'api-counters') {
      return Promise.resolve({ data: [], total: 0 });
    }
    if (resource === 'integrations') {
      const query = `sort=${encodeURIComponent(JSON.stringify(['companyName', 'ASC']))}&range=${encodeURIComponent(JSON.stringify([0, 9999]))}&filter=${encodeURIComponent(JSON.stringify({}))}`;

      return getAuthProviderFetchJson(`${superAdminApiServer}/integrations?${query}`).then(({ json }: any) => {
        const items = Array.isArray(json) ? json : json.data || json.items || json.results || [];
        const allData = items.map((item: any) => normalizeIntegrationRecord({ ...item, id: item.uuid }));

        const filter = params.filter || {};
        const filteredData = allData.filter((item: any) => {
          for (const key of Object.keys(filter)) {
            const filterVal = filter[key];
            if (filterVal === undefined || filterVal === null || filterVal === '') continue;
            const itemVal = item[key];
            if (typeof filterVal === 'boolean') {
              if (itemVal !== filterVal) return false;
            } else {
              if (String(itemVal ?? '') !== String(filterVal)) return false;
            }
          }
          return true;
        });

        const { field, order } = params.sort;
        filteredData.sort((a: any, b: any) => {
          const aVal = a[field] ?? '';
          const bVal = b[field] ?? '';
          if (aVal < bVal) return order === 'ASC' ? -1 : 1;
          if (aVal > bVal) return order === 'ASC' ? 1 : -1;
          return 0;
        });

        const { page, perPage } = params.pagination;
        const start = (page - 1) * perPage;
        const paginatedData = filteredData.slice(start, start + perPage);

        return {
          data: paginatedData,
          total: filteredData.length
        };
      });
    }
    return dataProvider.getList(resource, params).then((response: any) => {
      // For webhooks resource, map uuid to id since React Admin expects id field
      if (resource === 'webhooks') {
        const transformedData = response.data.map((item: any) => ({
          ...item,
          id: item.uuid
        }));
        return {
          ...response,
          data: transformedData
        };
      }
      if (resource === 'holiday-templates') {
        const transformedData = response.data.map((item: any) => ({
          ...item,
          id: item.uuid
        }));
        return { ...response, data: transformedData };
      }
      if (resource === 'schedule-templates') {
        const transformedData = response.data.map((item: any) => ({
          ...item,
          id: item.uuid
        }));
        return { ...response, data: transformedData };
      }
      if (resource === 'country-configs') {
        const transformedData = response.data.map((item: any) => myDataProvider.mapCountryConfigRecord(item));
        return { ...response, data: transformedData };
      }
      if (resource === 'business-rules-templates') {
        const transformedData = response.data.map((item: any) => ({ ...item, id: item.uuid }));
        return { ...response, data: transformedData };
      }
      if (resource === 'request-type-templates') {
        const transformedData = response.data.map((item: any) => ({ ...item, id: item.uuid }));
        return { ...response, data: transformedData };
      }
      if (resource === 'request-subtype-templates') {
        const transformedData = response.data.map((item: any) => ({ ...item, id: item.uuid }));
        return { ...response, data: transformedData };
      }
      // For webhook events resource, handle nested API structure
      if (resource.includes('/events')) {
        const transformedData = response.data.items.map((item: any) => ({
          ...item,
          id: item.uuid
        }));
        return {
          data: transformedData,
          total: response.data.total
        };
      }
      return response;
    });
  },
  getOne: (resource: any, params: any) => {
    if (process.env.NODE_ENV === 'development' && resource === 'integrations') {
      return import('./Pages/Hcm/integrationsMockData').then(({ integrationsMockData }) => {
        const item = integrationsMockData.find((i: any) => i.uuid === params.id);
        if (!item) return dataProvider.getOne(resource, params);
        return { data: { ...item, id: item.uuid } };
      });
    }

    return dataProvider.getOne(resource, params).then((response: any) => {
      // For integrations resource, map uuid to id since React Admin expects id field
      if (resource === 'integrations') {
        return {
          ...response,
          data: normalizeIntegrationRecord({
            ...response.data,
            id: response.data.uuid
          })
        };
      }
      // For webhooks resource, map uuid to id since React Admin expects id field
      if (resource === 'webhooks') {
        return {
          ...response,
          data: {
            ...response.data,
            id: response.data.uuid
          }
        };
      }
      if (resource === 'holiday-templates') {
        return { ...response, data: { ...response.data, id: response.data.uuid } };
      }
      if (resource === 'schedule-templates') {
        return { ...response, data: { ...response.data, id: response.data.uuid } };
      }
      if (resource === 'country-configs') {
        return { ...response, data: myDataProvider.mapCountryConfigRecord(response.data) };
      }
      if (resource === 'business-rules-templates') {
        const item = response.data;
        return { ...response, data: { ...item, id: item.uuid } };
      }
      if (resource === 'request-type-templates') {
        return { ...response, data: { ...response.data, id: response.data.uuid } };
      }
      if (resource === 'request-subtype-templates') {
        return { ...response, data: { ...response.data, id: response.data.uuid } };
      }
      // For webhook events resource, handle single event
      if (resource.includes('/events')) {
        return {
          ...response,
          data: {
            ...response.data,
            id: response.data.uuid
          }
        };
      }
      return response;
    });
  },
  getManyReference: (resource: any, params: any) => {
    // For webhook events resource, handle nested API structure
    if (resource.includes('/events')) {
      return dataProvider.getManyReference(resource, params).then((response: any) => {
        const transformedData = response.data.items.map((item: any) => ({
          ...item,
          id: item.uuid
        }));
        return {
          data: transformedData,
          total: response.data.total
        };
      });
    }
    return dataProvider.getManyReference(resource, params);
  },
  update: (resource: any, params: any) => {
    if (process.env.NODE_ENV === 'development' && (resource === 'integrations' || resource === 'integrations_config')) {
      console.log(`[DEV MOCK] update ${resource}`, params.data);
      if (resource === 'integrations_config') {
        const integrationConfig = params.data.integrationConfig ?? params.data.config;
        const previous = params.previousData || {};
        return Promise.resolve({
          data: normalizeIntegrationRecord({
            ...previous,
            integrationConfig,
            id: params.id
          })
        });
      }
      return Promise.resolve({ data: { ...params.data, id: params.id } });
    }
    if (resource === 'integrations') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(params.data)
      }).then(() => {
        // The endpoint returns [1] but we need to return the updated record
        return {
          data: { ...params.data, id: params.id }
        };
      });
    }
    if (resource === 'integrations_config') {
      const integrationConfig = params.data.integrationConfig ?? params.data.config;
      const previous = params.previousData || {};
      const fallbackRecord = () => ({
        data: normalizeIntegrationRecord({
          ...previous,
          integrationConfig,
          id: params.id
        })
      });
      return getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${params.id}/upsertConfig`, {
        method: 'POST',
        body: JSON.stringify(getIntegrationConfigUpsertBody(integrationConfig))
      }).then(() =>
        getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${params.id}`)
          .then(({ json }: any) => ({
            data: normalizeIntegrationRecord({
              ...json,
              id: json.uuid
            })
          }))
          .catch(fallbackRecord)
      );
    }
    if (resource === 'holiday-templates') {
      const { id, uuid, createdAt, updatedAt, deletedAt, ...fields } = params.data;
      return getAuthProviderFetchJson(`${superAdminApiServer}/holiday-templates/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify({ content: fields })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'schedule-templates') {
      const { id, uuid, createdAt, ...fields } = params.data;
      return getAuthProviderFetchJson(`${superAdminApiServer}/schedule-templates/${params.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ content: fields })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'country-configs') {
      const countryCode = String(params.data.country ?? params.id);
      const body = myDataProvider.countryConfigUpsertPayload({ ...params.data, country: params.data.country ?? countryCode });
      return getAuthProviderFetchJson(`${superAdminApiServer}/country-configs/${countryCode}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      }).then((response: any) => ({
        data: myDataProvider.mapCountryConfigRecord(response.json)
      }));
    }
    if (resource === 'business-rules-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/business-rules-templates/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify({ content: params.data.template })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'request-type-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/request-type-templates/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: params.data.active })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'request-subtype-templates') {
      const { nameId, translationKey, active, template } = params.data;
      return getAuthProviderFetchJson(`${superAdminApiServer}/request-subtype-templates/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify({ nameId, translationKey, active, template })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'partners') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/partners/${encodeURIComponent(params.id)}`, {
        method: 'PUT',
        body: JSON.stringify(partnerBody(params.data))
      }).then((response: any) => ({ data: response.json }));
    }
    if (resource !== 'posts') {
      // fallback to the default implementation
      return dataProvider.update(resource, params);
    }

    /**
     * For posts update only, convert uploaded image in base 64 and attach it to
     * the `picture` sent property, with `src` and `title` attributes.
     */

    // Freshly dropped pictures are File objects and must be converted to base64 strings
    const newPictures = params.data.pictures.filter((p: any) => p.rawFile instanceof File);
    const formerPictures = params.data.pictures.filter((p: any) => !(p.rawFile instanceof File));

    return Promise.all(newPictures.map(convertFileToBase64))
      .then((base64Pictures) =>
        base64Pictures.map((picture64) => ({
          src: picture64,
          title: `${params.data.title}`
        }))
      )
      .then((transformedNewPictures) =>
        dataProvider.update(resource, {
          data: {
            ...params.data,
            pictures: [...transformedNewPictures, ...formerPictures]
          },
          id: '',
          previousData: undefined
        })
      );
  },
  delete: (resource: any, params: any) => {
    if (resource === 'holiday-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/holiday-templates/${params.id}`, {
        method: 'DELETE'
      }).then(() => ({ data: { id: params.id } }));
    }
    if (resource === 'schedule-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/schedule-templates/${params.id}`, {
        method: 'DELETE'
      }).then(() => ({ data: { id: params.id } }));
    }
    if (resource === 'country-configs') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/country-configs/${params.id}`, {
        method: 'DELETE'
      }).then(() => ({ data: { id: params.id } }));
    }
    if (resource === 'business-rules-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/business-rules-templates/${params.id}`, {
        method: 'DELETE'
      }).then(() => ({ data: { id: params.id } }));
    }
    if (resource === 'request-type-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/request-type-templates/${params.id}`, {
        method: 'DELETE'
      }).then(() => ({ data: { id: params.id } }));
    }
    if (resource === 'request-subtype-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/request-subtype-templates/${params.id}`, {
        method: 'DELETE'
      }).then(() => ({ data: { id: params.id } }));
    }
    return dataProvider.delete(resource, params);
  },
  create: (resource: any, params: any) => {
    if (resource === 'holiday-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/holiday-templates`, {
        method: 'POST',
        body: JSON.stringify({ content: params.data })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'schedule-templates') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/schedule-templates`, {
        method: 'POST',
        body: JSON.stringify({ content: params.data })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'country-configs') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/country-configs`, {
        method: 'POST',
        body: JSON.stringify(myDataProvider.countryConfigUpsertPayload(params.data))
      }).then((response: any) => ({
        data: myDataProvider.mapCountryConfigRecord(response.json)
      }));
    }
    if (resource === 'business-rules-templates') {
      const { country, type, template } = params.data;
      return getAuthProviderFetchJson(`${superAdminApiServer}/business-rules-templates`, {
        method: 'POST',
        body: JSON.stringify({ country, type, content: template })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'request-type-templates') {
      const { country, name, active } = params.data;
      return getAuthProviderFetchJson(`${superAdminApiServer}/request-type-templates`, {
        method: 'POST',
        body: JSON.stringify({ country, name, active })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'request-subtype-templates') {
      const { requestTypeTemplateUuid, country, name, nameId, translationKey, active, template } = params.data;
      return getAuthProviderFetchJson(`${superAdminApiServer}/request-subtype-templates`, {
        method: 'POST',
        body: JSON.stringify({ requestTypeTemplateUuid, country, name, nameId, translationKey, active, template })
      }).then((response: any) => ({
        data: { ...response.json, id: response.json.uuid }
      }));
    }
    if (resource === 'partners') {
      return getAuthProviderFetchJson(`${superAdminApiServer}/partners`, {
        method: 'POST',
        body: JSON.stringify(partnerBody(params.data))
      }).then((response: any) => ({ data: response.json }));
    }
    return dataProvider.create(resource, params);
  },
  syncOrganisation: (organisationUuid: string) => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/customers/sync/${organisationUuid}`, { method: 'PUT' });
  },
  syncDashboards: () => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/dashboards/sync`, { method: 'POST' });
  },
  syncDataCubes: () => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/dashboards/data-cubes/sync`, { method: 'POST' });
  },
  updateIntegrationStatus: (integrationId: string, status: IntegrationStatus) => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${integrationId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    }).then(() => {
      return {
        data: { id: integrationId, status }
      };
    });
  },
  resetIntegration: (integrationId: string) => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${integrationId}/reset`, {
      method: 'POST'
    });
  },
  clearIntegrationCache: (integrationId: string) => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${integrationId}/cache/clear`, {
      method: 'POST'
    });
  },
  runIntegration: (integrationId: string) => {
    return getAuthProviderFetchJson(`${superAdminApiServer}/integrations/${integrationId}/sync`, {
      method: 'POST'
    });
  }
};

const App = () => (
  <div>
    <script src="https://jn07bxwfbxsk.statuspage.io/embed/script.js"></script>
    <Admin dataProvider={myDataProvider} loginPage={GoogleLoginPage} authProvider={GoogleAuthProvider} layout={AppLayout} requireAuth>
      <Resource name="companies" icon={BusinessIcon} list={CompanyList} show={CompanyShow} />
      <Resource name="profiles" icon={PeopleIcon} list={ProfilesList} show={ProfileShow} />
      <Resource name="users" icon={AccountBoxIcon} list={UsersList} show={UsersShow} />
      <Resource name="devices" icon={PhoneAndroidIcon} list={DevicesList} show={CompanyShow} />
      <Resource name="organizations" icon={CorporateFareIcon} list={OrganizationsList} show={CompanyShow} />
      <Resource name="customers" icon={AllInclusiveIcon} list={CustomersList} show={CustomerShow} edit={CustomerEdit} />
      <Resource name="integrations" icon={IntegrationInstructions} list={HcmList} show={HcmShow} edit={HcmEdit} options={{ label: 'HCM' }} />
      <CustomRoutes>
        <Route path="/integrations/:integrationId/employees-cache/:externalId" element={<EmployeeCacheDetail />} />
        <Route path="/integrations/:integrationId/requests-cache/:externalId" element={<RequestCacheDetail />} />
      </CustomRoutes>
      <Resource name="business-rules" icon={RuleIcon} list={BusinessRulesList} show={BusinessRuleShow} />
      <Resource name="dashboards" icon={StackedBarChartIcon} list={DashboardsList} edit={DashboardEdit} />
      <Resource name="cstools" icon={ConstructionIcon} list={CSToolsList} options={{ label: 'CS Tools' }} />
      <Resource name="dev-tools" icon={BuildIcon} list={DevToolsHome} options={{ label: 'Dev Tools' }} />
      <CustomRoutes>
        <Route path="/dev-tools/recalculate" element={<RecalculatePage />} />
      </CustomRoutes>
      <Resource name="pay-policy-templates" icon={TuneIcon} list={GatedPayPolicyTemplatesList} options={{ label: 'Pay-Policy Templates' }} />
      <CustomRoutes>
        <Route
          path="/pay-policy-templates/new"
          element={
            <TemplatesGate>
              <PayPolicyTemplateEditor />
            </TemplatesGate>
          }
        />
        <Route
          path="/pay-policy-templates/:uuid"
          element={
            <TemplatesGate>
              <PayPolicyTemplateEditor />
            </TemplatesGate>
          }
        />
      </CustomRoutes>
      <Resource name="webhooks" icon={WebhookIcon} list={WebhooksList} show={WebhookShow} />
      <Resource name="country-configs" list={CountryConfigsList} show={CountryConfigsShow} create={CountryConfigsCreate} edit={CountryConfigsEdit} options={{ label: 'Country Configs' }} />
      <Resource name="partners" icon={HandshakeIcon} list={PartnersList} show={PartnerShow} create={PartnerCreate} edit={PartnerEdit} options={{ label: 'Partners' }} />
      <CustomRoutes>
        <Route path="/partners/:uuid/config/create" element={<PartnerConfigEdit mode="create" />} />
        <Route path="/partners/:uuid/config/edit" element={<PartnerConfigEdit mode="edit" />} />
      </CustomRoutes>
      <Resource name="holiday-templates" create={HolidayTemplatesCreate} edit={HolidayTemplatesEdit} />
      <Resource name="api-counters" icon={BarChartIcon} list={ApiCountersDashboard} options={{ label: 'API Stats' }} />
      <Resource name="schedule-templates" create={ScheduleTemplatesCreate} edit={ScheduleTemplatesEdit} />
      <Resource name="business-rules-templates" create={BusinessRulesTemplatesCreate} edit={BusinessRulesTemplatesEdit} />
      <Resource name="request-type-templates" create={RequestTypeTemplatesCreate} edit={RequestTypeTemplatesEdit} />
      <Resource name="request-subtype-templates" create={RequestSubtypeTemplatesCreate} edit={RequestSubtypeTemplatesEdit} />
    </Admin>
  </div>
);

export default App;
